import React, { useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { LogOut, Users, CalendarDays, Map, MapPin, CalendarCheck2, CalendarRange, BarChart3, Settings, FileText, ShieldCheck, Car, LayoutDashboard, Link2, Bot, GraduationCap, Sparkles, Inbox, Users2, LayoutGrid, Calculator, TrendingUp } from "lucide-react";
import Logo from "@/components/Logo";
import Footer from "@/components/Footer";
import DashboardTab from "@/components/autoescuela/DashboardTab";
import StudentsTab from "@/components/autoescuela/StudentsTab";
import AppointmentsTab from "@/components/autoescuela/AppointmentsTab";
import CalendarTab from "@/components/autoescuela/CalendarTab";
import ContractsTab from "@/components/autoescuela/ContractsTab";
import CrearDocumentosTab from "@/components/autoescuela/CrearDocumentosTab";
import CircuitsTab from "@/components/autoescuela/CircuitsTab";
import LocationsTab from "@/components/autoescuela/LocationsTab";
import ReportsTab from "@/components/autoescuela/ReportsTab";
import StatisticsTab from "@/components/autoescuela/StatisticsTab";
import SettingsTab from "@/components/autoescuela/SettingsTab";
import DocumentsTab from "@/components/autoescuela/DocumentsTab";
import VehiclesTab from "@/components/autoescuela/VehiclesTab";
import TeachersTab from "@/components/autoescuela/TeachersTab";
import PlannerTab from "@/components/autoescuela/PlannerTab";
import PlannerBoardTab from "@/components/autoescuela/PlannerBoardTab";
import LinksTab from "@/components/autoescuela/LinksTab";
import KaiWorkspace from "@/components/autoescuela/KaiWorkspace";
import AnaWorkspace from "@/components/autoescuela/AnaWorkspace";
import ReferralsTab from "@/components/autoescuela/ReferralsTab";
import ContabilidadTab from "@/components/autoescuela/ContabilidadTab";
import SecurityTab from "@/components/security/SecurityTab";
import TodayDialog from "@/components/autoescuela/TodayDialog";
import TopNav from "@/components/autoescuela/TopNav";
import CorporateAlertsBadge from "@/components/autoescuela/CorporateAlertsBadge";
import SupremeAlertDialog from "@/components/autoescuela/SupremeAlertDialog";
import SecureLinksOverlay from "@/components/autoescuela/SecureLinksOverlay";
import RecaptchaHealthBanner from "@/components/autoescuela/RecaptchaHealthBanner";
import useDocumentTitle from "@/hooks/useDocumentTitle";

// V1.25.2 · Reagrupación adicional: Citas y Calendario dentro de Alumnos +
// acceso directo a Referidos globales dentro del grupo (además de en la ficha
// individual del alumno). 18 → 7 items raíz (–61 %).
const NAV_TREE = [
  { key: "inicio",       label: "Inicio",      icon: LayoutDashboard },
  {
    key: "_grp_alumnos", label: "Alumnos", icon: Users,
    children: [
      { key: "alumnos",     label: "Alumnos",     icon: Users },
      { key: "citas",       label: "Sesiones",    icon: CalendarDays },
      { key: "mesa",        label: "Planificación", icon: LayoutGrid },
      { key: "calendario",  label: "Calendario",  icon: CalendarRange },
      { key: "referidos",   label: "Referidos",   icon: Users2 },
    ],
  },
  {
    key: "_grp_contratos", label: "Contratos", icon: FileText,
    children: [
      { key: "contratos",   label: "Contratos",   icon: FileText },
      { key: "documentos",  label: "Documentos",  icon: FileText },
    ],
  },
  {
    key: "_grp_ia", label: "Agentes IA", icon: Bot,
    children: [
      { key: "kai_workspace", label: "KAI", icon: Bot },
      { key: "ana_workspace", label: "ANA", icon: Users2 },
    ],
  },
  {
    key: "_grp_profesorado", label: "Profesorado", icon: GraduationCap,
    children: [
      { key: "profesores",   label: "Profesores",  icon: GraduationCap },
      { key: "vehiculos",    label: "Vehículos",   icon: Car },
      { key: "circuitos",    label: "Circuitos",   icon: Map },
      { key: "ubicaciones",  label: "Ubicaciones", icon: MapPin },
    ],
  },
  { key: "contabilidad", label: "Contabilidad", icon: Calculator },
  { key: "estadisticas", label: "Estadísticas", icon: TrendingUp },
  { key: "reportes",     label: "Reportes",     icon: BarChart3 },
  { key: "config",       label: "Configuración",icon: Settings },
];

// Silence unused-import lint warnings for icons and components consumed via
// child workspaces / dynamic tabs:
void ShieldCheck; void Link2; void Sparkles; void Inbox; void LayoutGrid; void PlannerTab;

const TAB_TITLES = {
  inicio:      "Resumen diario",
  alumnos:     "Zona Profesorado",
  citas:       "Sesiones",
  mesa:        "Mesa de Planificación",
  calendario:  "Calendario",
  contratos:   "Contratos",
  crear_documentos: "Crear documentos",
  documentos:  "Documentos",
  kai_workspace: "KAI · Espacio de trabajo",
  ana_workspace: "ANA · Espacio de trabajo",
  referidos:   "Programa de Referidos",
  reportes:    "Reportes",
  enlaces:     "Enlaces institucionales",
  circuitos:   "Circuitos",
  ubicaciones: "Ubicaciones",
  vehiculos:   "Vehículos",
  profesores:  "Profesores",
  planificador:"Kai Planificador",
  contabilidad:"Contabilidad",
  seguridad:   "Seguridad",
  config:      "Configuración",
};

export default function Dashboard() {
  const { user, logout } = useAuth();
  const isTeacher = String(user?.role || "").toLowerCase() === "teacher";
  const perms = user?.permissions || {};
  // V1.46 · Vista de profesor: navegación plana según sus conmutadores de permiso.
  const teacherNav = React.useMemo(() => ([
    ...(perms.alumnos ? [{ key: "alumnos", label: "Alumnos", icon: Users }] : []),
    ...(perms.citas ? [{ key: "citas", label: "Sesiones", icon: CalendarDays }] : []),
    ...(perms.citas ? [{ key: "mesa", label: "Planificación", icon: LayoutGrid }] : []),
    ...(perms.calendario ? [{ key: "calendario", label: "Calendario", icon: CalendarRange }] : []),
    ...(perms.reportes ? [{ key: "reportes", label: "Reportes", icon: BarChart3 }] : []),
  ]), [perms.alumnos, perms.citas, perms.calendario, perms.reportes]);
  const [tab, setTab] = useState(() => (isTeacher ? "" : "inicio"));
  const [openToday, setOpenToday] = useState(false);
  const [linksOpen, setLinksOpen] = useState(false); // V1.54 · auditoría de enlaces
  // V1.39 · Dashboard operativo: los widgets pueden navegar a otras pestañas
  // pasando filtros iniciales (rango de bono, profesor, docs pendientes…) y/o
  // el alumno a enfocar (abrir Resumen). Cada pestaña destino consume el opt.
  const [studentsInitial, setStudentsInitial] = useState(null);
  useDocumentTitle(TAB_TITLES[tab] || "Panel");

  React.useEffect(() => {
    if (isTeacher && !teacherNav.some((t) => t.key === tab)) {
      setTab(teacherNav[0]?.key || "");
    }
  }, [isTeacher, tab, teacherNav]);

  const navigate = (nextTab, opts) => {
    if (isTeacher && !teacherNav.some((t) => t.key === nextTab)) return;
    setTab(nextTab);
    if (nextTab === "alumnos") setStudentsInitial(opts || null);
  };

  return (
    <div className="min-h-screen bg-neutral-50">
      {/* Header */}
      <header className="sticky top-0 z-30 bg-white border-b border-neutral-200">
        <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Logo variant="header" />
          </div>

          <div className="flex items-center gap-2">
            {/* V1.42.7 · Chip de identidad — Rol → Nombre. Ayuda a saber al
                instante con qué cuenta estás operando (Matriz / Franquicia /
                Profesor / Alumno / Admin). Sólo se muestra si hay usuario. */}
            {user && (() => {
              const raw = String(user.role || "").toLowerCase();
              const roleLabel =
                user.is_matrix ? "Matriz"
                : user.is_super_admin ? "Super Admin"
                : raw === "franquicia" ? "Franquicia"
                : raw === "profesor" || raw === "teacher" ? "Profesor"
                : raw === "alumno" ? "Alumno"
                : (user.role || "Usuario");
              const tone = user.is_matrix
                ? "bg-orange-100 text-orange-800 border-orange-200"
                : user.is_super_admin
                ? "bg-indigo-100 text-indigo-800 border-indigo-200"
                : raw === "franquicia"
                ? "bg-emerald-100 text-emerald-800 border-emerald-200"
                : raw === "profesor" || raw === "teacher"
                ? "bg-blue-100 text-blue-800 border-blue-200"
                : raw === "alumno"
                ? "bg-neutral-100 text-neutral-700 border-neutral-200"
                : "bg-neutral-100 text-neutral-700 border-neutral-200";
              return (
                <span
                  className={`inline-flex items-center gap-1 h-9 px-2 sm:px-2.5 rounded-full border text-[10px] sm:text-[11px] font-semibold ${tone}`}
                  title={user.email || ""}
                  data-testid="user-identity-chip"
                >
                  <span className="uppercase tracking-wide">{roleLabel}</span>
                  <span className="text-neutral-400">→</span>
                  <span className="truncate max-w-[70px] sm:max-w-[140px]">{user.name || user.email}</span>
                </span>
              );
            })()}
            {!isTeacher && <CorporateAlertsBadge onNavigate={navigate} />}
            <button
              data-testid="secure-links-button"
              onClick={() => setLinksOpen(true)}
              className="btn-secondary h-10 px-3"
              title="Auditoría de enlaces (72 h)"
            >
              <Link2 className="h-4 w-4" />
              <span className="hidden lg:inline">Enlaces</span>
            </button>
            {(!isTeacher || perms.citas || perms.calendario) && (
            <button
              data-testid="today-button"
              onClick={() => setOpenToday(true)}
              className="btn-primary h-10 px-3 sm:px-4"
            >
              <CalendarCheck2 className="h-4 w-4" />
              <span className="hidden sm:inline">Citas de hoy</span>
              <span className="sm:hidden">Hoy</span>
            </button>
            )}
            <button
              data-testid="logout-button"
              onClick={logout}
              className="btn-secondary h-10 px-3"
              title="Cerrar sesión"
            >
              <LogOut className="h-4 w-4" />
              <span className="hidden sm:inline">Salir</span>
            </button>
          </div>
        </div>

        {/* Tabs con grupos (V1.25) */}
        <TopNav items={isTeacher ? teacherNav : NAV_TREE} activeKey={tab} onSelect={navigate} />
      </header>

      {/* Welcome */}
      <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8 pt-6 sm:pt-8">
        <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">Panel de control</h1>
        <p className="text-neutral-500 text-sm mt-1">
          Hola {user?.name || "Profesor"} · gestiona alumnos, citas y clases.
        </p>
      </div>

      {/* Tab content — mismas keys, mismos componentes (sin cambios de lógica) */}
      <main className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
        {!isTeacher && <RecaptchaHealthBanner onNavigate={navigate} />}
        {isTeacher && teacherNav.length === 0 && (
          <div className="rounded-xl border border-amber-200 bg-amber-50 p-6 text-center text-sm text-amber-800" data-testid="teacher-no-permissions">
            Tu usuario no tiene permisos activados. Pide al administrador que active tus accesos desde tu ficha de profesor.
          </div>
        )}
        {tab === "inicio" && !isTeacher && <DashboardTab onNavigate={navigate} />}
        {tab === "alumnos" && (
          <StudentsTab
            initialFilter={studentsInitial}
            onFilterConsumed={() => setStudentsInitial(null)}
            onNavigate={navigate}
          />
        )}
        {tab === "citas" && <AppointmentsTab />}
        {tab === "mesa" && <PlannerBoardTab />}
        {tab === "calendario" && <CalendarTab />}
        {tab === "contratos" && <ContractsTab />}
        {tab === "crear_documentos" && <CrearDocumentosTab />}
        {tab === "documentos" && <DocumentsTab />}
        {tab === "kai_workspace" && <KaiWorkspace />}
        {tab === "ana_workspace" && <AnaWorkspace />}
        {tab === "bandeja_kai" && <KaiWorkspace initialSection="bandeja" />}
        {tab === "referidos" && <ReferralsTab />}
        {tab === "reportes" && <ReportsTab />}
        {tab === "estadisticas" && <StatisticsTab />}
        {tab === "enlaces" && <LinksTab />}
        {tab === "circuitos" && <CircuitsTab />}
        {tab === "ubicaciones" && <LocationsTab />}
        {tab === "vehiculos" && <VehiclesTab />}
        {tab === "profesores" && <TeachersTab />}
        {tab === "planificador" && <KaiWorkspace initialSection="planificador" />}
        {tab === "contabilidad" && <ContabilidadTab />}
        {tab === "seguridad" && <SecurityTab />}
        {tab === "config" && <SettingsTab onNavigateToTab={navigate} />}
      </main>

      <TodayDialog open={openToday} onOpenChange={setOpenToday} />
      <SupremeAlertDialog />
      <SecureLinksOverlay open={linksOpen} onClose={() => setLinksOpen(false)} />
      <Footer />
    </div>
  );
}
